import { useState } from "react";
import { useDebounce } from "../../../hooks/useDebounce";
import { DataTable } from "../../../components/ui/data-table";
import { Input } from "../../../components/ui/input";
import { DateRangePicker } from "../../../components/ui/date-range-picker";
import { type DateRange } from "react-day-picker";
import { columns } from "./visit-history-columns";
import { useVisitHistory } from "../hooks/useVisitHistory";
import { Visit } from "../types/visit";
import { VisitDetailsDialog } from "./VisitDetailsDialog";
import { PrintVisitDialog } from "./PrintVisitDialog";
import { Search, Calendar } from "lucide-react";

export function VisitHistory() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [calendarRange, setCalendarRange] = useState<DateRange>();

  // Convert calendar range to date range tuple for API
  const handleCalendarChange = (range: DateRange | undefined) => {
    setCalendarRange(range);
    setDateRange([range?.from ?? null, range?.to ?? null]);
  };
  const [selectedVisit, setSelectedVisit] = useState<Visit | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isPrintOpen, setIsPrintOpen] = useState(false);

  const { data: visits, isLoading } = useVisitHistory({
    search: debouncedSearch,
    dateRange,
  });

  const handleViewDetails = (visit: Visit) => {
    setSelectedVisit(visit);
    setIsDetailsOpen(true);
  };

  const handlePrintRecord = (visit: Visit) => {
    setSelectedVisit(visit);
    setIsPrintOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-background to-background rounded-lg -z-10" />
        <div className="relative space-y-4 p-6 rounded-lg border border-border/50 bg-background/50 backdrop-blur-sm">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-foreground">Visit History</h1>
              <p className="text-sm text-muted-foreground">
                View and manage patient visit records
              </p>
            </div>
          </div>

          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search visits by patient name or type..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-background/50 backdrop-blur-sm"
              />
            </div>
            <div className="relative min-w-[240px] bg-background/50 backdrop-blur-sm rounded-md border border-input">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <div className="pl-9">
                <DateRangePicker
                  value={calendarRange}
                  onChange={handleCalendarChange}
                  placeholder="Filter by date range"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="rounded-lg border border-border/50 bg-background/50 backdrop-blur-sm shadow-md">
        <DataTable
          columns={columns({
            onViewDetails: handleViewDetails,
            onPrintRecord: handlePrintRecord,
          })}
          data={visits || []}
          isLoading={isLoading}
          rowSize="sm"
        />
      </div>

      {/* Dialogs */}
      {selectedVisit && (
        <>
          <VisitDetailsDialog
            visit={selectedVisit}
            open={isDetailsOpen}
            onOpenChange={setIsDetailsOpen}
          />
          <PrintVisitDialog
            visit={selectedVisit}
            open={isPrintOpen}
            onOpenChange={setIsPrintOpen}
          />
        </>
      )}
    </div>
  );
}
